
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { CuisineType, FoodType } from "@/types/recipe";

type SortOption = "newest" | "oldest" | "most_viewed" | "title_asc" | "title_desc";

export interface Recipe {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  visibility: "public" | "subscription";
  cuisine_type: CuisineType | null;
  food_type: FoodType | null;
  views: number | null;
  created_at: string;
  user: {
    username: string;
    creator_type: string[] | null;
    nationality: string | null;
  };
}

export interface Creator {
  id: string;
  username: string;
  avatar_url: string | null;
  bio: string | null;
  creator_type: string[] | null;
  nationality: string | null;
  recipes: { count: number }[];
  created_at: string;
  views: number;
}

interface UseLibraryDataParams {
  search: string;
  selectedCreatorType: string | null;
  selectedNationality: string | null;
  selectedCuisineType: CuisineType | null;
  selectedFoodType: FoodType | null;
  currentPage: number;
  sortBy: SortOption;
  viewMode: "recipes" | "creators";
  recipesPerPage: number;
}

export function useLibraryData({
  search,
  selectedCreatorType,
  selectedNationality,
  selectedCuisineType,
  selectedFoodType,
  currentPage,
  sortBy,
  viewMode,
  recipesPerPage,
}: UseLibraryDataParams) {
  const { data, isLoading } = useQuery({
    queryKey: ['library-data', search, selectedCreatorType, selectedNationality, selectedCuisineType, selectedFoodType, currentPage, sortBy, viewMode],
    queryFn: async () => {
      if (viewMode === "creators") {
        let query = supabase
          .from('profiles')
          .select(`
            id,
            username,
            avatar_url,
            bio,
            creator_type,
            nationality,
            created_at,
            views,
            recipes:recipes(count)
          `);

        if (search) {
          query = query.ilike('username', `%${search}%`);
        }

        if (selectedCreatorType) {
          // Use ? operator for JSONB array containment
          query = query.filter('creator_type', 'cs', `["${selectedCreatorType}"]`);
        }

        if (selectedNationality) {
          query = query.eq('nationality', selectedNationality);
        }

        switch (sortBy) {
          case "newest":
            query = query.order('created_at', { ascending: false });
            break;
          case "oldest":
            query = query.order('created_at', { ascending: true });
            break;
          case "most_viewed":
            query = query.order('views', { ascending: false });
            break;
          case "title_asc":
            query = query.order('username', { ascending: true });
            break;
          case "title_desc":
            query = query.order('username', { ascending: false });
            break;
        }

        query = query.range((currentPage - 1) * recipesPerPage, currentPage * recipesPerPage - 1);

        const { data: creatorData, error } = await query;
        if (error) throw error;
        
        return (creatorData || []).map(creator => ({
          ...creator,
          creator_type: creator.creator_type || null,
          views: creator.views || 0
        })) as Creator[];
      } else {
        let query = supabase
          .from('recipes')
          .select(`
            id,
            title,
            description,
            image_url,
            visibility,
            cuisine_type,
            food_type,
            views,
            created_at,
            user:profiles!inner (
              username,
              creator_type,
              nationality
            )
          `)
          .eq('visibility', 'public');

        if (search) {
          query = query.ilike('title', `%${search}%`);
        }

        if (selectedCreatorType) {
          // Use proper JSONB array containment operator
          query = query.filter('profiles.creator_type', 'cs', `["${selectedCreatorType}"]`);
        }

        if (selectedNationality) {
          query = query.eq('profiles.nationality', selectedNationality);
        }

        if (selectedCuisineType) {
          query = query.eq('cuisine_type', selectedCuisineType);
        }

        if (selectedFoodType) {
          query = query.eq('food_type', selectedFoodType);
        }

        switch (sortBy) {
          case "newest":
            query = query.order('created_at', { ascending: false });
            break;
          case "oldest":
            query = query.order('created_at', { ascending: true });
            break;
          case "most_viewed":
            query = query.order('views', { ascending: false });
            break;
          case "title_asc":
            query = query.order('title', { ascending: true });
            break;
          case "title_desc":
            query = query.order('title', { ascending: false });
            break;
        }

        query = query.range((currentPage - 1) * recipesPerPage, currentPage * recipesPerPage - 1);

        const { data, error } = await query;
        if (error) {
          console.error("Error fetching recipes:", error);
          throw error;
        }
        
        console.log("Fetched recipes:", data);
        return data as Recipe[];
      }
    },
  });

  return { data: data as Creator[] | Recipe[], isLoading };
}
